import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Switch,
  FormControlLabel,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Chip
} from '@mui/material';
import {
  CloudUpload,
  Image,
  VideoLibrary,
  Delete as DeleteIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { categoryService, storyService } from '../services/api';
import { ModalGridContainer, ModalFormField } from './common/ModalGrid';
import VideoPickerModal from './stories/VideoPickerModal';

const StoryForm = ({ open, onClose, story = null, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const isEdit = Boolean(story);

  const [formData, setFormData] = useState({
    titleEn: '',
    titleGr: '',
    category: '',
    mediaType: 'image',
    isPublished: true,
    scheduledAt: '',
    order: 0
  });

  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [uploadLoading, setUploadLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedStoryFile, setSelectedStoryFile] = useState(null);
  const [selectedThumbnailFile, setSelectedThumbnailFile] = useState(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState(null);
  const [generatingThumbnail, setGeneratingThumbnail] = useState(false);

  // "Video share" story: links to an existing video instead of uploading media.
  const [linkedVideo, setLinkedVideo] = useState(null);
  const [videoPickerOpen, setVideoPickerOpen] = useState(false);
  const isVideoShare = formData.mediaType === 'videoShare';

  const linkedVideoTitle = (video) =>
    video?.titleGr || video?.titleEn || video?.title || '';

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (open) {
      if (story) {
        setFormData({
          titleEn: story.titleEn || '',
          titleGr: story.titleGr || '',
          category: story.category?._id || '',
          mediaType: story.mediaType || 'image',
          isPublished: story.isPublished !== undefined ? story.isPublished : true,
          scheduledAt: story.scheduledAt ? new Date(story.scheduledAt).toISOString().slice(0, 16) : '',
          order: story.order || 0
        });

        setLinkedVideo(story.linkedVideo || null);

        // Set preview URL for existing story - prioritize storyUrl for videos to show actual video
        if (story.mediaType === 'video' && story.storyUrl) {
          setPreviewUrl(story.storyUrl);
        } else if (story.thumbnailUrl) {
          setPreviewUrl(story.thumbnailUrl);
        } else if (story.storyUrl && story.mediaType === 'image') {
          setPreviewUrl(story.storyUrl);
        }
      } else {
        setFormData({
          titleEn: '',
          titleGr: '',
          category: '',
          mediaType: 'image',
          isPublished: true,
          scheduledAt: '',
          order: 0
        });
        setPreviewUrl(null);
        setThumbnailPreviewUrl(null);
        setLinkedVideo(null);
      }
      setSelectedStoryFile(null);
      setSelectedThumbnailFile(null);
      setGeneratingThumbnail(false);
      setErrors({});
    }
  }, [story, open]);

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAll();
      setCategories(response.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const extractVideoThumbnail = async (file) => {
    return new Promise((resolve, reject) => {
      setGeneratingThumbnail(true);
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      const videoUrl = URL.createObjectURL(file);
      video.src = videoUrl;

      video.onloadedmetadata = () => {
        // Set canvas size to story aspect ratio (9:16)
        canvas.width = 720;
        canvas.height = 1280;

        // Seek to 1 second (or start if video is shorter)
        video.currentTime = Math.min(1, video.duration / 2);
      };

      video.onseeked = () => {
        try {
          // Draw video frame to canvas
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          // Convert canvas to blob
          canvas.toBlob((blob) => {
            URL.revokeObjectURL(videoUrl);
            setGeneratingThumbnail(false);

            if (blob) {
              const thumbnailFile = new File([blob], `thumbnail_${Date.now()}.jpg`, {
                type: 'image/jpeg'
              });

              // Create preview URL for display
              const thumbnailUrl = URL.createObjectURL(blob);
              setThumbnailPreviewUrl(thumbnailUrl);

              resolve(thumbnailFile);
            } else {
              reject(new Error('Failed to generate thumbnail'));
            }
          }, 'image/jpeg', 0.85);
        } catch (error) {
          URL.revokeObjectURL(videoUrl);
          setGeneratingThumbnail(false);
          reject(error);
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(videoUrl);
        setGeneratingThumbnail(false);
        reject(new Error('Failed to load video'));
      };
    });
  };

  const handleStoryFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (!isImage && !isVideo) {
      setErrors({ storyFile: t('stories.invalidFileType') });
      return;
    }

    // Validate file size (50MB max for videos, 10MB for images)
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrors({ storyFile: t('stories.fileTooLarge') });
      return;
    }

    setSelectedStoryFile(file);
    setErrors({ ...errors, storyFile: '' });

    // Update media type based on file
    const mediaType = isVideo ? 'video' : 'image';
    setFormData(prev => ({ ...prev, mediaType }));

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    // Auto-generate thumbnail for videos
    if (isVideo) {
      try {
        const thumbnailFile = await extractVideoThumbnail(file);
        setSelectedThumbnailFile(thumbnailFile);
      } catch (error) {
        console.error('Error generating thumbnail:', error);
        setErrors({ ...errors, thumbnail: t('stories.thumbnailGenerationFailed') || 'Failed to generate thumbnail' });
      }
    }
  };

  const handleThumbnailFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type (only images for thumbnails)
    if (!file.type.startsWith('image/')) {
      setErrors({ thumbnailFile: t('stories.invalidThumbnailType') });
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setErrors({ thumbnailFile: t('stories.fileTooLarge') });
      return;
    }

    setSelectedThumbnailFile(file);
    setErrors({ ...errors, thumbnailFile: '' });
  };

  const handleRemoveStoryFile = () => {
    setSelectedStoryFile(null);
    setPreviewUrl(null);
  };

  const handleRemoveThumbnailFile = () => {
    setSelectedThumbnailFile(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.category) {
      newErrors.category = t('stories.categoryRequired');
    }

    if (isVideoShare) {
      // A video-share story carries no uploaded media — it needs a linked video.
      if (!linkedVideo?._id) {
        newErrors.linkedVideo = t('stories.videoRequired');
      }
    } else if (!isEdit && !selectedStoryFile) {
      // Check if story file is required (new story)
      newErrors.storyFile = t('stories.storyFileRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setUploadLoading(true);
    try {
      // Create FormData following CategoryForm pattern
      const submitData = new FormData();

      // Add text fields
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        // Always send title fields (even if empty) so backend can clear them
        if (key === 'titleEn' || key === 'titleGr') {
          submitData.append(key, value || '');
        } else if (value !== '' && value !== null && value !== undefined) {
          submitData.append(key, value);
        }
      });

      // Video-share stories send the linked video id and no media file.
      if (isVideoShare && linkedVideo?._id) {
        submitData.append('linkedVideo', linkedVideo._id);
      }

      // Add files
      if (!isVideoShare && selectedStoryFile) {
        submitData.append('story', selectedStoryFile);
      }
      if (selectedThumbnailFile) {
        submitData.append('thumbnail', selectedThumbnailFile);
      }

      // Submit
      if (isEdit) {
        await storyService.update(story._id, submitData);
      } else {
        await storyService.create(submitData);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMsg = error.response?.data?.error || error.message;
      setErrors({ general: t('error.saveFailed') + ': ' + errorMsg });
    } finally {
      setUploadLoading(false);
    }
  };

  const handleClose = () => {
    if (!uploadLoading) {
      onClose();
    }
  };

  const getDialogTitle = () => {
    if (isEdit && story) {
      const storyName = i18n.language === 'gr' ? story.titleGr : story.titleEn;
      return `${t('stories.edit')}: ${storyName || 'Story'}`;
    }
    return t('stories.add');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit
      }}
    >
      <DialogTitle>
        {getDialogTitle()}
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* Basic Information */}
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          {t('stories.basicInfo')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <ModalGridContainer spacing={2}>
          <ModalFormField>
            <TextField
              name="titleGr"
              label={t('stories.titleGr')}
              value={formData.titleGr}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              error={!!errors.titleGr}
              helperText={errors.titleGr}
              disabled={uploadLoading}
            />
          </ModalFormField>

          <ModalFormField>
            <TextField
              name="titleEn"
              label={t('stories.titleEn')}
              value={formData.titleEn}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              error={!!errors.titleEn}
              helperText={errors.titleEn}
              disabled={uploadLoading}
            />
          </ModalFormField>

          <ModalFormField>
            <FormControl fullWidth variant="outlined" required error={!!errors.category}>
              <InputLabel>{t('stories.category')} *</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                label={`${t('stories.category')} *`}
                disabled={uploadLoading}
              >
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category.nameGr} / {category.nameEn}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && (
                <Typography variant="caption" color="error">
                  {errors.category}
                </Typography>
              )}
            </FormControl>
          </ModalFormField>

          <ModalFormField>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isPublished}
                  onChange={handleChange}
                  name="isPublished"
                  disabled={uploadLoading}
                />
              }
              label={t('stories.published')}
            />
          </ModalFormField>

          <ModalFormField>
            <TextField
              fullWidth
              type="datetime-local"
              name="scheduledAt"
              label="Προγραμματισμένη Δημοσίευση / Scheduled Publishing"
              value={formData.scheduledAt}
              onChange={handleChange}
              variant="outlined"
              disabled={uploadLoading}
              slotProps={{
                inputLabel: { shrink: true }
              }}
              helperText="Αφήστε κενό για άμεση δημοσίευση / Leave empty for immediate publishing"
            />
          </ModalFormField>
        </ModalGridContainer>

        {/* Story Media Upload */}
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mt: 3 }}>
          {t('stories.storyMedia')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* Story type: upload media, or link to an existing video */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>{t('stories.storyType')}</InputLabel>
          <Select
            value={isVideoShare ? 'videoShare' : 'upload'}
            label={t('stories.storyType')}
            onChange={(e) => {
              const videoShare = e.target.value === 'videoShare';
              setFormData((prev) => ({
                ...prev,
                // Going back to upload resets to 'image'; the real type is then
                // inferred from the picked file, as before.
                mediaType: videoShare ? 'videoShare' : 'image'
              }));
              if (videoShare) {
                handleRemoveStoryFile();
              } else {
                setLinkedVideo(null);
              }
              setErrors({});
            }}
          >
            <MenuItem value="upload">{t('stories.typeUpload')}</MenuItem>
            <MenuItem value="videoShare">{t('stories.typeVideoShare')}</MenuItem>
          </Select>
        </FormControl>

        {isVideoShare && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('stories.videoShareHelp')}
            </Typography>

            {linkedVideo ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  mt: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2
                }}
              >
                <Avatar
                  variant="rounded"
                  src={linkedVideo.thumbnailUrl || undefined}
                  sx={{ width: 96, height: 60, bgcolor: 'grey.200' }}
                >
                  <VideoLibrary />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {linkedVideoTitle(linkedVideo)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('stories.selectedVideo')}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={() => setVideoPickerOpen(true)}
                >
                  {t('stories.changeVideo')}
                </Button>
              </Box>
            ) : (
              <Button
                variant="outlined"
                startIcon={<LinkIcon />}
                onClick={() => setVideoPickerOpen(true)}
                sx={{ mt: 1 }}
              >
                {t('stories.selectVideo')}
              </Button>
            )}

            {errors.linkedVideo && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {errors.linkedVideo}
              </Typography>
            )}
          </Box>
        )}

        <Box
          sx={{
            display: isVideoShare ? 'none' : 'flex',
            alignItems: 'flex-start',
            gap: 3,
            flexWrap: 'wrap'
          }}
        >
          {/* Video/Image Preview */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('stories.preview')}
            </Typography>
            {formData.mediaType === 'video' && previewUrl ? (
              <Box sx={{ position: 'relative', width: 200, height: 356, borderRadius: 2, overflow: 'hidden' }}>
                <video
                  src={previewUrl}
                  controls
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    border: '2px solid #e0e0e0'
                  }}
                />
              </Box>
            ) : (
              <>
                <Avatar
                  src={previewUrl}
                  sx={{
                    width: 200,
                    height: 356,
                    bgcolor: 'grey.200',
                    border: '2px solid',
                    borderColor: 'divider'
                  }}
                  variant="rounded"
                >
                  {!previewUrl && (formData.mediaType === 'video' ? <VideoLibrary sx={{ fontSize: 60, color: 'grey.400' }} /> : <Image sx={{ fontSize: 60, color: 'grey.400' }} />)}
                </Avatar>
              </>
            )}
          </Box>

          {/* Thumbnail Preview (for videos) */}
          {formData.mediaType === 'video' && (thumbnailPreviewUrl || generatingThumbnail) && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('stories.thumbnail')} ({t('stories.autoGenerated')})
              </Typography>
              {generatingThumbnail ? (
                <Box sx={{ width: 100, height: 178, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', borderRadius: 2 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : (
                <Avatar
                  src={thumbnailPreviewUrl}
                  sx={{
                    width: 100,
                    height: 178,
                    bgcolor: 'grey.200',
                    border: '2px solid',
                    borderColor: 'primary.main'
                  }}
                  variant="rounded"
                />
              )}
            </Box>
          )}

          {/* Upload Controls */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {isEdit ? t('stories.replaceMedia') : t('stories.uploadMedia')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={uploadLoading ? <CircularProgress size={20} /> : <CloudUpload />}
                disabled={uploadLoading}
                size="small"
              >
                {t('stories.selectFile')}
                <input
                  type="file"
                  hidden
                  accept="image/*,video/*"
                  onChange={handleStoryFileSelect}
                />
              </Button>

              {previewUrl && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleRemoveStoryFile}
                  disabled={uploadLoading}
                  size="small"
                >
                  {t('stories.removeFile')}
                </Button>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary">
              {t('stories.mediaFormats')}
            </Typography>

            {errors.storyFile && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {errors.storyFile}
              </Alert>
            )}

            {selectedStoryFile && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t('stories.selectedFile')}: {selectedStoryFile.name}
              </Alert>
            )}
          </Box>
        </Box>

        {/* Optional Thumbnail Upload */}
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mt: 3 }}>
          {t('stories.customThumbnail')} ({t('common.optional')})
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<Image />}
            disabled={uploadLoading}
            size="small"
          >
            {selectedThumbnailFile ? selectedThumbnailFile.name : t('stories.selectThumbnail')}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleThumbnailFileSelect}
            />
          </Button>

          {selectedThumbnailFile && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleRemoveThumbnailFile}
              disabled={uploadLoading}
              size="small"
            >
              {t('stories.removeThumbnail')}
            </Button>
          )}
        </Box>

        {errors.thumbnailFile && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errors.thumbnailFile}
          </Alert>
        )}

        {errors.general && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.general}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1, justifyContent: 'flex-end' }}>
        <Button
          onClick={handleClose}
          disabled={uploadLoading}
          size="large"
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={uploadLoading}
          sx={{ minWidth: 120 }}
        >
          {uploadLoading ? t('common.saving') : (isEdit ? t('common.update') : t('common.create'))}
        </Button>
      </DialogActions>

      <VideoPickerModal
        open={videoPickerOpen}
        onClose={() => setVideoPickerOpen(false)}
        onSelect={(video) => {
          setLinkedVideo(video);
          setErrors((prev) => ({ ...prev, linkedVideo: '' }));
        }}
      />
    </Dialog>
  );
};

export default StoryForm;